import { uploadCustomerPricingData } from "@crescentParts/Services/CustomerPricingService";
import CustomApplicationState from "@crescentParts/Store/CustomApplicationState";
import getCustomerPricing from "@crescentParts/Store/Data/CustomerPricing/Handler/CustomerPricingHandler";
import uploadCustomerPricing from "@crescentParts/Store/Data/CustomerPricing/Handler/UploadCustomerPricingHandler";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonIcon, ButtonPresentationProps } from "@insite/mobius/Button";
import FileUpload from "@insite/mobius/FileUpload";
import RefreshCw from "@insite/mobius/Icons/RefreshCw";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import Pagination, { PaginationPresentationProps } from "@insite/mobius/Pagination";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect, useRef, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css } from "styled-components";
import * as XLSX from "xlsx";

export interface PricingWidgetStyles {
    mainWrapper?: InjectableCss;
    uploadFileButton?: any;
    cancelUploadButton?: any;
    spinner?: any;
    uploadingButtonLabel?: any;
    uploadFileButtonLabel?: any;
    uploadLinkTooltipText?: TypographyPresentationProps;
    downloadTemplateButton?: ButtonPresentationProps;
    buttonWrapper?: InjectableCss;
    pagination?: PaginationPresentationProps;
}

const styles: PricingWidgetStyles = {
    mainWrapper: {
        css: css`
            min-height: 50vh;
            width: 100%;
            padding: 15px;
            padding-bottom: 20px;
        `,
    },
    uploadFileButton: {
        css: css``,
    },
    cancelUploadButton: {
        css: css`
            margin-left: 10px;
        `,
    },
    spinner: {
        size: 22,
        css: css`
            margin-right: 8px;
        `,
    },
    uploadingButtonLabel: {
        css: css`
            font-weight: bold;
        `,
    },
    uploadFileButtonLabel: {
        css: css`
            font-weight: bold;
            font-size: 15px;
        `,
    },
    uploadLinkTooltipText: {
        css: css`
            margin-bottom: 15px;
        `,
    },
    downloadTemplateButton: {
        css: css`
            margin-bottom: 15px;
        `,
        variant: "primary",
    },
    buttonWrapper: {
        css: css`
            margin-top: 15px;
            display: flex;
            gap: 10px;
            justify-content: right;
        `,
    },
};

const mapStateToProps = (state: CustomApplicationState) => {
    return {
        customerPricingExportList: state.cpeState.customerPricingData,
        isAuthenticated: state.context.session?.isAuthenticated,
        signInUrl: getPageLinkByPageType(state, "SignInPage")?.url,
        currentLocation: getLocation(state),
    };
};
const mapDispatchToProps = {
    getCustomerPricing,
    uploadCustomerPricingData,
    uploadCustomerPricing,
};
type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

const TableWrapper = styled.div`
    margin: 20px 0;
    overflow-x: auto;
`;

// Table element
const Table = styled.table`
    width: 100%;
    border-collapse: collapse;
    border: 1px solid #ccc;
`;

// Table Header
const Th = styled.th`
    border: 1px solid #ccc;
    padding: 10px;
    text-align: left;
    background-color: #f0f0f0;
`;

// Table Data Cell
const Td = styled.td`
    border: 1px solid #ccc;
    padding: 10px;
`;

// Optional: Table Row hover effect
const Tr = styled.tr`
    &:hover {
        background-color: #e0e0e0ff;
    }
`;

const SpinnerWrapper = styled.div`
    margin: auto;
    min-height: 45vh;
    justify-content: center;
    align-items: center;
    display: flex;
`;

export const CustomerPricingWidget: React.FunctionComponent<Props> = ({
    isAuthenticated,
    signInUrl,
    currentLocation,
    history,
    getCustomerPricing,
    uploadCustomerPricing,
    customerPricingExportList,
    fields,
}: Props) => {
    const {
        exportList = [],
        pagination: {
            totalItemCount = 0,
            page = 1,
            pageSize = 9,
            pageSizeOptions = [],
            prevPageUri = "",
            nextPageUri = "",
        } = {}, // default empty object for pagination
        isLoading = false,
    } = customerPricingExportList || {}; // default empty object if undefined

    const [file, setFile] = useState<File | null>(null);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadCancelled, setUploadCancelled] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const MAX_FILE_SIZE_MB = 5;
    const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

    const fileInputRef = useRef<HTMLInputElement>({} as HTMLInputElement);

    const normalizeHeader = (value: any) =>
        String(value || "")
            .trim()
            .toLowerCase()
            .replace(/\s+/g, " ");

    const handleFileChange = (event: React.ChangeEvent<any>) => {
        const selectedFile = event.target.files?.[0];
        setUploadCancelled(false);
        setError(null);

        if (!selectedFile) {
            setFile(null);
            return;
        }

        if (selectedFile.size > MAX_FILE_SIZE_BYTES) {
            setError(`File size must be less than ${MAX_FILE_SIZE_MB} MB.`);
            setFile(null);
            if (fileInputRef.current) fileInputRef.current.value = "";
            return;
        }

        setFile(selectedFile);
    };

    const fetchCustomerPricing = (page: number = 1) => {
        // Get current date/time in ISO format and encode it for the URL
        const currentDate = new Date().toISOString();
        const encodedDate = encodeURIComponent(currentDate);

        const pimExportPayload = {
            aPiparameter: `/api/v2/CustomerPricing/ExportHistory?page=${page}&date=%27${encodedDate}%27`,
        };

        getCustomerPricing(pimExportPayload);
    };

    const changePage = (newPageIndex: number) => {
        fetchCustomerPricing(newPageIndex);
    };

    const extractIdsFromCSV = (text: string) => {
        const lines = text.split(/\r?\n/).filter(Boolean);

        if (!lines.length) {
            throw new Error("The file is empty.");
        }

        const headers = lines[0].split(",").map(normalizeHeader);
        const productIdIndex = headers.findIndex(h => h === "product id");

        if (productIdIndex === -1) {
            throw new Error('Missing required column "Product ID".');
        }

        const ids = lines
            .slice(1)
            .map(line => line.split(",")[productIdIndex])
            .map(v => (v ? String(v).trim() : ""))
            .filter(Boolean);

        if (!ids.length) {
            throw new Error("No product IDs found in the file.");
        }

        uploadCustomerPricing({
            aPiparameter: ids,
        });
    };

    const extractIdsFromXLSX = (arrayBuffer: ArrayBuffer) => {
        const workbook = XLSX.read(arrayBuffer, { type: "array" });
        const sheetName = workbook.SheetNames[0];

        if (!sheetName) {
            throw new Error("No worksheet found in the file.");
        }

        const sheet = workbook.Sheets[sheetName];
        const data: any[][] = XLSX.utils.sheet_to_json(sheet, { header: 1 });

        if (!data.length) {
            throw new Error("The file is empty.");
        }

        const headers = data[0].map(normalizeHeader);
        const productIdIndex = headers.findIndex(h => h === "product id");

        if (productIdIndex === -1) {
            throw new Error('Missing required column "Product ID".');
        }

        const ids = data
            .slice(1)
            .map(row => row?.[productIdIndex])
            .map(v => (v ? String(v).trim() : ""))
            .filter(Boolean);

        if (!ids.length) {
            throw new Error("No product IDs found in the file.");
        }

        uploadCustomerPricing({
            aPiparameter: ids,
        });
    };

    const uploadFileHandler = () => {
        if (!file) return;
        if (file.size > MAX_FILE_SIZE_BYTES) {
            setError(`File size must be less than ${MAX_FILE_SIZE_MB} MB.`);
            setIsUploading(false);
            return;
        }
        const allowedExtensions = ["csv", "xls", "xlsx"];
        const extension = file.name.split(".").pop()?.toLowerCase();
        if (!extension || !allowedExtensions.includes(extension)) {
            setError("Only CSV, XLS, or XLSX files are allowed.");
            return;
        }

        setIsUploading(true);
        setUploadCancelled(false);
        setError(null);

        const reader = new FileReader();

        reader.onload = e => {
            const result = e.target?.result;
            if (!result) {
                setError("Failed to read file.");
                setIsUploading(false);
                return;
            }

            try {
                if (extension === "csv") {
                    extractIdsFromCSV(result as string);
                } else {
                    extractIdsFromXLSX(result as ArrayBuffer);
                }
            } catch (err: any) {
                setError(err.message || "Error processing file.");
            } finally {
                setIsUploading(false);
                setFile(null);
                if (fileInputRef.current) fileInputRef.current.value = "";
            }
        };

        if (extension === "csv") {
            reader.readAsText(file);
        } else {
            reader.readAsArrayBuffer(file);
        }
    };

    const cancelUploadHandler = () => {
        setIsUploading(false);
        setUploadCancelled(true);
        setFile(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    const downloadTemplateHandler = () => {
        const link = document.createElement("a");
        link.href = fields.templateFile;
        link.download = "Product_template.xlsx"; // Add a filename here
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const startRecord = ((page ?? 1) - 1) * (pageSize ?? 0) + 1;
    const endRecord = Math.min(startRecord + (pageSize ?? 0) - 1, totalItemCount ?? 0);

    useEffect(() => {
        fetchCustomerPricing(1);
    }, []);

    // --- authentication redirect logic ---
    if (isAuthenticated === undefined) {
        // Waiting for auth state to resolve, avoid showing UI for a split second
        return (
            <SpinnerWrapper>
                <LoadingSpinner />
            </SpinnerWrapper>
        );
    }

    if (!isAuthenticated && signInUrl) {
        const returnUrl = currentLocation.pathname;
        const redirectUrl = returnUrl === "/" ? signInUrl : `${signInUrl}?returnUrl=${encodeURIComponent(returnUrl)}`;

        history.push(redirectUrl);
        return (
            <SpinnerWrapper>
                <LoadingSpinner />
            </SpinnerWrapper>
        );
    }

    return (
        <StyledWrapper {...styles.mainWrapper}>
            {fields.templateFile && (
                <>
                    <Typography as="p" {...styles.uploadLinkTooltipText}>
                        {siteMessage(fields.fileInstructions)}
                    </Typography>
                    <Button {...styles.downloadTemplateButton} onClick={downloadTemplateHandler}>
                        {translate("Download Template")}
                    </Button>
                </>
            )}

            {uploadCancelled && <Typography color="warning">{siteMessage("Upload Cancelled")}</Typography>}
            {error && (
                <Typography color="danger" style={{ marginLeft: "10px" }}>
                    {siteMessage(error)}
                </Typography>
            )}

            <FileUpload
                inputRef={fileInputRef}
                fileName={file?.name || ""}
                accept=".xls,.xlsx,.csv"
                label="Upload a File"
                labelPosition="top"
                onFileChange={handleFileChange}
            />
            <StyledWrapper {...styles.buttonWrapper}>
                <Button {...styles.cancelUploadButton} disabled={!isUploading} onClick={cancelUploadHandler}>
                    {siteMessage("Cancel")}
                </Button>
                <Button {...styles.uploadFileButton} disabled={!file || isUploading} onClick={uploadFileHandler}>
                    {isUploading ? (
                        <>
                            <LoadingSpinner {...styles.spinner} />
                            <Typography {...styles.uploadingButtonLabel}>{siteMessage("Processing...")}</Typography>
                        </>
                    ) : (
                        <Typography {...styles.uploadFileButtonLabel}>{siteMessage("Upload File")}</Typography>
                    )}
                </Button>
            </StyledWrapper>
            <TableWrapper style={{ minHeight: "55vh" }}>
                <StyledWrapper
                    style={{
                        display: "flex",
                        justifyContent: "space-between",
                        padding: "5px",
                    }}
                >
                    {totalItemCount && !isLoading ? (
                        <p>
                            Showing {startRecord}–{endRecord} of {totalItemCount ?? 0} Records
                        </p>
                    ) : (
                        <p></p>
                    )}
                    <ButtonIcon
                        style={{ color: "#1C355E", cursor: "pointer" }}
                        onClick={() => {
                            fetchCustomerPricing(1);
                        }}
                        className="menu-trigger-button-icon"
                        src={RefreshCw}
                    />
                </StyledWrapper>
                <Table>
                    <thead>
                        <Tr>
                            <Th style={{ maxWidth: "30px" }}>Sr No</Th>
                            <Th>Job Number</Th>
                            <Th>Status</Th>
                        </Tr>
                    </thead>
                    <tbody>
                        {!isLoading && (
                            <>
                                {exportList &&
                                    exportList?.map((job, index) => (
                                        <Tr key={job.jobNumber}>
                                            <Td>{((page ?? 1) - 1) * (pageSize ?? 0) + index + 1}</Td>
                                            <Td>{job.jobNumber}</Td>
                                            <Td>{job.status}</Td>
                                        </Tr>
                                    ))}
                            </>
                        )}
                    </tbody>
                </Table>

                {isLoading && (
                    <SpinnerWrapper>
                        <LoadingSpinner />
                    </SpinnerWrapper>
                )}

                {totalItemCount !== 0 ? (
                    <Pagination
                        {...styles.pagination}
                        resultsCount={totalItemCount}
                        currentPage={page}
                        resultsPerPage={pageSize}
                        resultsPerPageOptions={pageSizeOptions}
                        onChangePage={changePage}
                        onChangeResultsPerPage={() => {}}
                        prevPage={prevPageUri}
                        nextPage={nextPageUri}
                    />
                ) : null}
            </TableWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(CustomerPricingWidget)),
    definition: {
        group: "Basic",
        icon: "diamond",
        displayName: "Customer Pricing Widget",
        fieldDefinitions: [
            {
                displayName: "Template File",
                name: "templateFile",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                displayName: "File Instructions",
                name: "fileInstructions",
                editorTemplate: "TextField",
                defaultValue:
                    "Upload an Excel (.xls or .xlsx) or CSV (Comma-Separated Values) file to fetch product pricing information.",
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
